// Enemy system: spawning by district weights, chase movement with v1's
// dynamic-speed guarantee, walk animation, Phase 5 behavioral abilities
// (telegraphed signature moves) and paint slicks, plus sensei spawns.

import * as THREE from 'three';
import { LANES, SPAWN_DISTANCE } from '@/config/tuning';
import { selectEnemyType, dynamicEnemySpeed, SENSEI_DEF, type EnemyDef } from '@/content/enemies';
import type { Rng } from '@/core/rng';
import { buildEnemyRig, animateEnemyRig, combatStance, type EnemyRig } from './avatars';
import { freshBehavior, updateBehavior, type BehaviorState } from './behaviors';
import type { CarSystem } from '@/entities/cars';

export interface Enemy {
  rig: EnemyRig;
  def: EnemyDef;
  inCombat: boolean;
  animTime: number;
  behavior: BehaviorState;
  hitRadius: number;
  airborneY: number;
  blocked: boolean;
  isSensei: boolean;
}

export interface PaintSlick {
  mesh: THREE.Mesh;
  triggered: boolean;
}

const slickGeo = new THREE.CircleGeometry(0.55, 12);
slickGeo.rotateX(-Math.PI / 2);

export class EnemySystem {
  readonly enemies: Enemy[] = [];
  readonly slicks: PaintSlick[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    private readonly rng: Rng,
  ) {}

  /** Hard active-count cap (Phase 7): rigs are hierarchical, not instanced,
   *  so their draw-call cost is bounded here instead. */
  private static readonly MAX_ACTIVE = 8;

  /** Spawn on a pavement lane (v1 obstacle-manager.js:209-231). */
  spawn(districtId: string, blockedAt: (x: number, z: number) => boolean): void {
    if (this.enemies.length >= EnemySystem.MAX_ACTIVE) return;
    let lane = this.rng.next() < 0.5 ? 0 : 3;
    if (blockedAt(LANES[lane as 0 | 3], SPAWN_DISTANCE)) {
      lane = lane === 0 ? 3 : 0;
      if (blockedAt(LANES[lane as 0 | 3], SPAWN_DISTANCE)) return; // Both pavements blocked
    }
    const def = selectEnemyType(districtId, this.rng.next());
    this.spawnAt(def, LANES[lane as 0 | 3], false);
  }

  /** Belt-gated sensei: black gi with the promoted belt's color (Phase 5). */
  spawnSensei(beltColor: number): void {
    const lane = this.rng.next() < 0.5 ? 0 : 3;
    const enemy = this.spawnAt(SENSEI_DEF, LANES[lane], true);
    // Belt sash in the promotion color
    const sash = new THREE.Mesh(
      new THREE.BoxGeometry(0.44, 0.08, 0.26),
      new THREE.MeshLambertMaterial({ color: beltColor }),
    );
    sash.position.y = 0.42;
    enemy.rig.group.add(sash);
  }

  private spawnAt(def: EnemyDef, x: number, isSensei: boolean): Enemy {
    const rig = buildEnemyRig(def, this.rng);
    rig.group.position.set(x, 0.24, SPAWN_DISTANCE);
    if (isSensei) rig.group.scale.setScalar(1.15);
    this.scene.add(rig.group);
    const enemy: Enemy = {
      rig,
      def,
      inCombat: false,
      animTime: this.rng.next() * 10,
      behavior: freshBehavior(),
      hitRadius: 0.6,
      airborneY: 0,
      blocked: false,
      isSensei,
    };
    this.enemies.push(enemy);
    return enemy;
  }

  update(
    dt: number,
    gameSpeed: number,
    playerPos: THREE.Vector3,
    playerJumping: boolean,
    cars: CarSystem,
  ): void {
    const direction = new THREE.Vector3();
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      if (!enemy) continue;
      const pos = enemy.rig.group.position;
      enemy.animTime += dt;

      if (enemy.inCombat) {
        combatStance(enemy.rig, enemy.animTime * 3);
        continue;
      }

      // World scroll (v1 obstacle-manager.js:2434)
      pos.z += gameSpeed * dt * 60;

      // Signature ability (Phase 5)
      const fx = updateBehavior(enemy.def, enemy.behavior, enemy.rig, {
        z: pos.z,
        playerJumping,
        dt,
      });
      enemy.hitRadius = fx.hitRadius;
      enemy.airborneY = fx.airborneY;
      enemy.blocked = fx.blocked;
      if (fx.dropSlick) this.dropSlick(pos.x, pos.z);

      // Chase the player (v1 :2449-2497)
      direction.subVectors(playerPos, pos);
      direction.y = 0;
      direction.normalize();
      let walkSpeed = dynamicEnemySpeed(enemy.def, gameSpeed) * fx.speedFactor;

      // Simplified traffic stop: wait while a car is close on a street lane
      if (Math.abs(pos.x) < 3.2 || (Math.abs(pos.x) < 4.2 && Math.abs(direction.x) > 0.3)) {
        for (const car of cars.cars) {
          if (
            Math.abs(car.group.position.z - pos.z) < 5 &&
            Math.abs(car.group.position.x - pos.x) < 2.2
          ) {
            walkSpeed = 0;
            break;
          }
        }
      }

      if (walkSpeed > 0) {
        pos.addScaledVector(direction, walkSpeed * dt * 60);
        enemy.rig.group.rotation.y = Math.atan2(direction.x, direction.z);
      }

      // Mirror-jump arc offsets the whole rig vertically
      pos.y = 0.24 + enemy.airborneY;

      animateEnemyRig(enemy.rig, enemy.def, enemy.animTime, walkSpeed);

      if (pos.z > 15) this.despawn(i);
    }

    // Paint slicks scroll with the world and expire behind the player
    const dz = gameSpeed * dt * 60;
    for (let i = this.slicks.length - 1; i >= 0; i--) {
      const slick = this.slicks[i];
      if (!slick) continue;
      slick.mesh.position.z += dz;
      if (slick.mesh.position.z > 12) {
        this.scene.remove(slick.mesh);
        this.slicks.splice(i, 1);
      }
    }
  }

  private dropSlick(x: number, z: number): void {
    const mesh = new THREE.Mesh(
      slickGeo,
      new THREE.MeshLambertMaterial({ color: 0x8b2be2, transparent: true, opacity: 0.85 }),
    );
    mesh.position.set(x, 0.1, z);
    this.scene.add(mesh);
    this.slicks.push({ mesh, triggered: false });
  }

  despawn(index: number): void {
    const enemy = this.enemies[index];
    if (!enemy) return;
    this.scene.remove(enemy.rig.group);
    this.enemies.splice(index, 1);
  }

  remove(enemy: Enemy): void {
    const index = this.enemies.indexOf(enemy);
    if (index >= 0) this.despawn(index);
  }

  clear(): void {
    for (let i = this.enemies.length - 1; i >= 0; i--) this.despawn(i);
    for (const slick of this.slicks) this.scene.remove(slick.mesh);
    this.slicks.length = 0;
  }
}
